import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgencyClient } from '../../../api/agencies.api';
import { UserAvatar } from '../../ui/UserAvatar';
import { Plus, Search, Star, X, Icon } from '../../ui/Icon';
import ClientFormModal from './ClientFormModal';
import { useClientSearch } from './useClientSearch';

export interface PickedClient {
  id: string;
  name: string;
  email: string | null;
}

interface Props {
  agencyId: string;
  picked: PickedClient[];
  onChange: (picked: PickedClient[]) => void;
  max: number;
}

const RESULTS_SHOWN = 6;

const toPicked = (c: AgencyClient): PickedClient => ({ id: c.id, name: c.name, email: c.email });

/**
 * ADR-0037: «Who is this trip for?» — clients from the agency's book, or a new
 * one that joins it. The first is the lead traveller, who owns the trip (ADR-0033).
 */
export default function ClientPicker({ agencyId, picked, onChange, max }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const search = useClientSearch(agencyId, query);
  const pickedIds = new Set(picked.map(p => p.id));
  const results = search.clients.filter(c => !pickedIds.has(c.id)).slice(0, RESULTS_SHOWN);
  const full = picked.length >= max;

  const add = (c: PickedClient) => { onChange([...picked, c]); setQuery(''); };
  const remove = (id: string) => onChange(picked.filter(p => p.id !== id));
  const makeLead = (id: string) => onChange([...picked.filter(p => p.id === id), ...picked.filter(p => p.id !== id)]);

  return (
    <div className="space-y-4">
      {picked.length > 0 && (
        <ul className="rounded-2xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
          {picked.map((p, i) => (
            <li key={p.id} className="flex items-center gap-3 px-4 py-2.5">
              <UserAvatar name={p.name} size={30} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {p.name}
                  {i === 0 && <span className="ml-2 text-[11px] font-semibold text-teal-600 dark:text-teal-400">{t('agency.panel.lead')}</span>}
                </p>
                <p className={`text-[12px] truncate ${p.email ? 'text-slate-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {p.email ?? t('agency.clients.picker.noEmail')}
                </p>
              </div>
              {i > 0 && (
                <button type="button" className="btn-icon w-8 h-8 text-slate-400 hover:text-teal-600" onClick={() => makeLead(p.id)}
                  aria-label={t('agency.clients.picker.makeLead')} title={t('agency.clients.picker.makeLead')}>
                  <Icon as={Star} size={14} />
                </button>
              )}
              <button type="button" className="btn-icon w-8 h-8 text-slate-400 hover:text-red-500" onClick={() => remove(p.id)}
                aria-label={t('agency.clients.picker.remove')}>
                <Icon as={X} size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!full && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden">
          <label className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <Icon as={Search} size={15} className="text-slate-400 shrink-0" />
            <input className="flex-1 bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
              value={query} onChange={e => setQuery(e.target.value)} placeholder={t('agency.clients.picker.search')}
              aria-label={t('agency.clients.picker.search')} />
          </label>
          <ul className="max-h-64 overflow-y-auto">
            {results.map(c => (
              <li key={c.id}>
                <button type="button" onClick={() => add(toPicked(c))}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60">
                  <UserAvatar name={c.name} size={26} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-slate-800 dark:text-slate-200 truncate">{c.name}</span>
                    {c.email && <span className="block text-[12px] text-slate-400 truncate">{c.email}</span>}
                  </span>
                  <Icon as={Plus} size={14} className="text-teal-600 shrink-0" />
                </button>
              </li>
            ))}
            {!search.loading && results.length === 0 && (
              <li className="px-4 py-3 text-[13px] text-slate-400">
                {query.trim() ? t('agency.clients.picker.noMatch') : t('agency.clients.picker.emptyBook')}
              </li>
            )}
          </ul>
          <button type="button" onClick={() => setCreating(true)}
            className="w-full flex items-center gap-2 px-4 py-3 border-t border-slate-100 dark:border-slate-800 text-sm font-semibold text-teal-700 dark:text-teal-300 hover:bg-teal-50/60 dark:hover:bg-teal-900/20">
            <Icon as={Plus} size={15} />
            {query.trim() ? t('agency.clients.picker.createNamed', { name: query.trim() }) : t('agency.clients.new')}
          </button>
        </div>
      )}

      {creating && (
        <ClientFormModal agencyId={agencyId} initialName={query.trim()} onClose={() => setCreating(false)}
          onSaved={c => { setCreating(false); add(toPicked(c)); }} />
      )}
    </div>
  );
}
